import React, { useState, useEffect } from 'react';
import Table from '../components/common/Table';
import Pagination from '../components/common/Pagination';
import SearchBar from '../components/common/SearchBar';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import ConfirmDialog from '../components/common/ConfirmDialog';
import CompanyForm from '../components/company/CompanyForm';
import CompanyDetailsModal from '../components/company/CompanyDetailsModal';
import EditCompanyModal from '../components/company/EditCompanyModal';
import { companyApi } from '../api/companyApi';
import { usePagination } from '../hooks/usePagination';
import { useDebounce } from '../hooks/useDebounce';
import { formatDate } from '../utils/formatDate';
import { Building2, Plus, Eye, Edit, Trash2, Power, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);

  const { page, setPage, limit, total, setTotal, pages } = usePagination(1, 10);
  const debouncedSearch = useDebounce(search, 400);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await companyApi.getCompanies({
        page,
        limit,
        search: debouncedSearch,
        status: statusFilter,
        sortBy,
        sortOrder
      });
      setCompanies(res.data.companies || []);
      setTotal(res.data.pagination.total || 0);
    } catch (err) {
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [page, limit, debouncedSearch, statusFilter, sortBy, sortOrder]);

  const handleSort = (accessor) => {
    if (sortBy === accessor) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(accessor);
      setSortOrder('asc');
    }
  };

  const handleCreateCompany = async (formData) => {
    setSubmitting(true);
    try {
      await companyApi.createCompany(formData);
      toast.success('Company tenant onboarded successfully!');
      setIsAddModalOpen(false);
      fetchCompanies();
    } catch (err) {
      toast.error(err.message || 'Failed to create company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCompany = async (companyId, formData) => {
    setSubmitting(true);
    try {
      await companyApi.updateCompany(companyId, formData);
      toast.success('Company tenant details and employee limit updated!');
      setIsEditModalOpen(false);
      setCompanyToEdit(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.message || 'Failed to update company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;
    setSubmitting(true);
    try {
      await companyApi.deleteCompany(companyToDelete._id);
      toast.success('Company deleted successfully');
      setCompanyToDelete(null);
      fetchCompanies();
    } catch (err) {
      toast.error('Failed to delete company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (company) => {
    const newStatus = company.status === 'Active' ? 'Suspended' : 'Active';
    try {
      await companyApi.updateCompany(company._id, { status: newStatus });
      toast.success(`Company status changed to ${newStatus}`);
      fetchCompanies();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const columns = [
    {
      header: 'Company Name',
      accessor: 'name',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center space-x-3">
          <img
            src={row.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=150&auto=format&fit=crop&q=80'}
            alt={row.name}
            className="w-10 h-10 rounded-xl object-cover border border-slate-100"
          />
          <div>
            <p className="font-semibold text-slate-900 font-display">{row.name}</p>
            <p className="text-xs text-slate-500">{row.industry}</p>
          </div>
        </div>
      )
    },
    {
      header: 'ID Prefix',
      accessor: 'employeeIdPrefix',
      cell: (row) => (
        <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded-lg bg-green-50 border border-green-100 text-green-700">
          {row.employeeIdPrefix}
        </span>
      )
    },
    {
      header: 'Admin Account',
      accessor: 'adminId',
      cell: (row) =>
        row.adminId ? (
          <div>
            <p className="font-medium text-slate-900 text-xs">{row.adminId.name}</p>
            <p className="text-[11px] text-slate-500">{row.adminId.email}</p>
          </div>
        ) : (
          <span className="text-slate-500 text-xs italic">Unassigned</span>
        )
    },
    {
      header: 'Employee Limit & Usage',
      accessor: 'currentEmployeeCount',
      cell: (row) => {
        const limit = row.employeeLimit || 50;
        const current = row.currentEmployeeCount || 0;
        return (
          <span className="font-semibold text-slate-900 text-xs px-3 py-1.5 rounded-xl bg-white border border-slate-100 inline-block font-mono">
            {current} / {limit} Max
          </span>
        );
      }
    },
    {
      header: 'Status',
      accessor: 'status',
      sortable: true,
      cell: (row) => <Badge variant={row.status}>{row.status}</Badge>
    },
    {
      header: 'Onboarded',
      accessor: 'onboardedAt',
      sortable: true,
      cell: (row) => <span className="text-xs text-slate-500">{formatDate(row.onboardedAt)}</span>
    },
    {
      header: 'Actions',
      accessor: '_id',
      cell: (row) => (
        <div className="flex items-center space-x-1.5">
          <button
            onClick={() => {
              setSelectedCompanyId(row._id);
              setIsDetailsOpen(true);
            }}
            title="View Details"
            className="p-1.5 rounded-lg text-slate-500 hover:text-green-700 hover:bg-slate-50 transition"
          >
            <Eye className="w-4 h-4" />
          </button>

          <button
            onClick={() => {
              setCompanyToEdit(row);
              setIsEditModalOpen(true);
            }}
            title="Edit Company & Employee Limit"
            className="p-1.5 rounded-lg text-slate-500 hover:text-green-700 hover:bg-green-600/10 transition"
          >
            <Edit className="w-4 h-4" />
          </button>

          <button
            onClick={() => handleToggleStatus(row)}
            title={row.status === 'Active' ? 'Suspend Company' : 'Reactivate Company'}
            className={`p-1.5 rounded-lg transition ${
              row.status === 'Active'
                ? 'text-slate-500 hover:text-amber-600 hover:bg-amber-500/10'
                : 'text-amber-600 hover:text-emerald-600 hover:bg-emerald-500/10'
            }`}
          >
            <Power className="w-4 h-4" />
          </button>

          <button
            onClick={() => setCompanyToDelete(row)}
            title="Delete Company"
            className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-500/10 transition"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900 font-display">Company Directory</h2>
          <p className="text-xs text-slate-500 mt-1">Manage onboarded company tenants and employee caps</p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="px-5 py-2.5 rounded-xl bg-green-700 hover:bg-green-600 text-white font-semibold text-xs shadow-lg shadow-green-700/30 flex items-center space-x-2 transition"
        >
          <Plus className="w-4 h-4" />
          <span>Add New Company</span>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-white border border-slate-100 shadow-sm">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by company name, industry, or prefix..."
          className="w-full sm:w-80"
        />

        <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs text-slate-600 focus:outline-none focus:border-green-600 transition"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="py-16 text-center text-xs text-slate-500">Loading companies...</div>
      ) : companies.length === 0 ? (
        <div className="py-12 text-center text-xs text-slate-500">No company tenants match your query.</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={companies}
            onSort={handleSort}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />

          <Pagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            onPageChange={(p) => setPage(p)}
          />
        </>
      )}

      {/* Add Company Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Onboard New Company Tenant"
        maxWidth="max-w-2xl"
      >
        <CompanyForm
          onSubmit={handleCreateCompany}
          onCancel={() => setIsAddModalOpen(false)}
          loading={submitting}
        />
      </Modal>

      {/* Edit Company Modal */}
      <EditCompanyModal
        company={companyToEdit}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setCompanyToEdit(null);
        }}
        onSubmit={handleUpdateCompany}
        loading={submitting}
      />

      {/* Company Details Modal */}
      <CompanyDetailsModal
        companyId={selectedCompanyId}
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedCompanyId(null);
        }}
        onEdit={(comp) => {
          setCompanyToEdit(comp);
          setIsEditModalOpen(true);
        }}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={!!companyToDelete}
        onClose={() => setCompanyToDelete(null)}
        onConfirm={handleDeleteCompany}
        title="Delete Company Tenant?"
        message={`Are you sure you want to delete ${companyToDelete?.name}? This will remove all associated admin accounts, employee records, and ID sequence configs.`}
        confirmText="Delete Tenant"
        loading={submitting}
      />
    </div>
  );
};

export default Companies;
